// Turns a country's core.json (stored by column, to keep it small) into name entries. Shared by
// the app and the website's name pages (netlify/lib/names-core.ts), so it imports nothing else.
import { nameKey, stripAccents } from "./keys";
import type { CoreData, GenderGroup, NameEntry } from "./types";

export interface RawCore {
  v: string;
  built: string;
  country: CoreData["country"];
  y0: number;
  latest: number;
  births: CoreData["births"];
  origins: string[];
  sources: CoreData["sources"];
  names: {
    n: string[];
    g: string;
    fs: number[];
    rf: number[];
    rm: number[];
    cf: number[];
    cm: number[];
    r5f: number[];
    r5m: number[];
    tf: (number | null)[];
    tm: (number | null)[];
    sf: string[];
    sm: string[];
    py: number[];
    pc: number[];
    pr: number[];
    fy: number[];
    tg: number[];
    sy: number[];
    o: number[];
    m: string[];
    p?: string[];
    ms: string;
  };
}

export function decodeCore(raw: RawCore): CoreData {
  const c = raw.names;
  const entries: NameEntry[] = new Array(c.n.length);
  const byKey = new Map<string, NameEntry>();
  for (let i = 0; i < c.n.length; i++) {
    const name = c.n[i];
    const letters = stripAccents(name).replace(/[^A-Za-z]/g, "");
    const ms = c.ms.charAt(i);
    const e: NameEntry = {
      idx: i,
      name,
      key: nameKey(name),
      g: c.g.charAt(i) as GenderGroup,
      fs: c.fs[i],
      r: [c.rf[i], c.rm[i]],
      c: [c.cf[i], c.cm[i]],
      r5: [c.r5f[i], c.r5m[i]],
      t: [c.tf[i], c.tm[i]],
      s: [c.sf[i], c.sm[i]],
      peakYear: c.py[i],
      peakCount: c.pc[i],
      peakRank: c.pr[i],
      firstYear: c.fy[i],
      tags: c.tg[i],
      syl: c.sy[i],
      origin: c.o[i] >= 0 ? raw.origins[c.o[i]] : null,
      // a lower-case source flag marks an origin note standing in for a meaning
      meaning: ms === "a" || ms === "w" ? "" : c.m[i],
      originNote: ms === "a" || ms === "w" ? c.m[i] : "",
      pron: c.p?.[i] ?? "",
      msrc: ms === "W" || ms === "A" ? ms : ms === "a" ? "A" : ms === "w" ? "W" : "",
      len: letters.length,
      initial: letters.charAt(0).toUpperCase(),
    };
    entries[i] = e;
    byKey.set(e.key, e);
  }
  return {
    v: raw.v,
    built: raw.built,
    country: raw.country,
    y0: raw.y0,
    latest: raw.latest,
    births: raw.births,
    origins: raw.origins,
    sources: raw.sources,
    entries,
    byKey,
  };
}
